import { useEffect, useState } from "react";
import { COMMISSION_BASIS_LABEL, type SalesSettings } from "@clevis/shared-types";
import { Card } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";

// Commission on this job, next to the payment card: the office sees every
// seller's; a tech sees their own (Brandon: "a place on the job for it, with
// the piece rate"). The server decides who sees what; rates never ride on
// the job document.

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

interface Row {
  uid: string;
  name: string;
  invoiceNumber: number;
  rateBps: number;
  baseCents: number;
  earnedCents: number;
  totalCents: number;
}

export function JobPay({ tenantId, jobId, rev }: { tenantId: string; jobId: string; rev: number }) {
  const [data, setData] = useState<{ basis: SalesSettings["commissionBasis"]; when: SalesSettings["commissionWhen"]; rows: Row[] } | null>(null);
  useEffect(() => {
    let dead = false;
    void call<{ basis: SalesSettings["commissionBasis"]; when: SalesSettings["commissionWhen"]; rows: Row[] }>("getJobCommission", { tenantId, jobId }, { mutation: false })
      .then((r) => !dead && setData(r))
      .catch(() => !dead && setData(null));
    return () => {
      dead = true;
    };
  }, [tenantId, jobId, rev]);
  if (!data || data.rows.length === 0) return null;
  const byPerson = new Map<string, { name: string; rateBps: number; earned: number; total: number; invoices: number[] }>();
  for (const r of data.rows) {
    const p = byPerson.get(r.uid) ?? { name: r.name || "Teammate", rateBps: r.rateBps, earned: 0, total: 0, invoices: [] };
    p.earned += r.earnedCents;
    p.total += r.totalCents;
    p.invoices.push(r.invoiceNumber);
    byPerson.set(r.uid, p);
  }
  return (
    <Card className="space-y-1 py-2 text-xs">
      <div className="font-semibold text-ink">Commission on this job</div>
      {[...byPerson.values()].map((p) => (
        <div key={p.name + p.invoices.join()} className="flex flex-wrap items-baseline justify-between gap-x-3 text-ink">
          <span>
            {p.name} · {p.rateBps ? `${p.rateBps / 100}% of ${COMMISSION_BASIS_LABEL[data.basis]}` : "no rate set"}
            <span className="text-steel"> · invoice{p.invoices.length === 1 ? "" : "s"} #{p.invoices.join(", #")}</span>
          </span>
          <span>
            <strong>{money(p.earned)}</strong>
            {data.when === "collected" && p.total !== p.earned ? <span className="text-steel"> earned of {money(p.total)} (as the customer pays)</span> : null}
          </span>
        </div>
      ))}
    </Card>
  );
}
